// Copyright (c) 2021 WiseTime. All rights reserved.

import { Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap, map } from "rxjs/operators"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { DeleteRecentNarrativeRequestSchema, RecentNarrativeSchema } from "../../../generated/wisetime/user/preferences/v4/preferences_pb"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, preferencesService } from "../../../app/rpc"
import { create } from "@bufbuild/protobuf"
import type { Empty } from "@bufbuild/protobuf/wkt"

type DeleteRecentNarrativeRpc = (request: Init<typeof DeleteRecentNarrativeRequestSchema>) => Observable<Empty>

export const deleteRecentNarrative = (deleteRecentNarrativeRpc: DeleteRecentNarrativeRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.deleteRecentNarrative.match),
      mergeMap(({ payload }) =>
        deleteRecentNarrativeRpc(payload).pipe(
          map(_ => Slice.deleteCachedNarrative(
            payload.recentNarrative ? create(RecentNarrativeSchema, payload.recentNarrative) : undefined
          )),
          catchError(handleError),
        )
      ),
    )

const deleteRecentNarrativeRpc = (request: Init<typeof DeleteRecentNarrativeRequestSchema>): Observable<Empty> =>
  executorMutation(() => preferencesService.deleteRecentNarrative(create(DeleteRecentNarrativeRequestSchema, request)))

const handleError = (e: unknown): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({ id: "msg_delete_narrative_failed_permission_denied", message: "Failed to delete the recent narrative. You do not have permission to delete the recent narrative." }),
        messageType: "ERROR",
      }))
    }
  }

  return of(showSnackBar({
    message: t({ id: "msg_delete_narrative_failed", message: "Failed to delete the recent narrative. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

export default deleteRecentNarrative(deleteRecentNarrativeRpc)
